import AppLocale from 'lngProvider';
import { sendErrorToSentry } from '@src/util/sentry/utils';
import { GLOSSARY_ITEMS } from '../components/reports/shared/constants';

/** Shown when an observation has no glossary abbreviation (Art. 28 g). */
export const MISSING_OBSERVATION = '-';

/** Exception codes stored by services, e.g. paid_leave or shift_replacement. */
const INTERNAL_CODE = /^[a-z0-9]+(_[a-z0-9]+)+$/;

/** Abbreviations are stored in upper case, with or without the dots (P.H.E, PGR). */
const spellingKey = value => value.replace(/\./g, '');

const labelKey = value => value.toLocaleLowerCase();

/** Glossary spelling by spelling key. */
const GLOSSARY_SPELLING = new Map(GLOSSARY_ITEMS.map(({ short }) => [spellingKey(short), short]));

/**
 * Glossary spelling by label, in every locale: the edit drawer stores the label of the absence
 * reason in the language of whoever saved it. The label ids follow the glossary convention
 * work.direction.glossary.item.<abbreviation without dots>.
 */
const GLOSSARY_SPELLING_BY_LABEL = GLOSSARY_ITEMS.reduce((acc, { short }) => {
  Object.values(AppLocale).forEach(({ messages }) => {
    const label = messages[`work.direction.glossary.item.${spellingKey(short)}`];
    if (label) acc.set(labelKey(label), short);
  });
  return acc;
}, new Map());

const reportedCodes = new Set();

const reportMissingAbbreviation = code => {
  if (reportedCodes.has(code)) return;
  reportedCodes.add(code);
  sendErrorToSentry(new Error(`DT report observation without glossary abbreviation: ${code}`), {
    context: 'formatObservation',
    code,
  });
};

/**
 * Observation as the DT reports must show it (Art. 28 g):
 * - an exception code becomes its glossary abbreviation (paid_leave → P.G.R.);
 * - a glossary label becomes its abbreviation (Ausencia justificada → A.J.);
 * - an abbreviation stored with another spelling uses the glossary one (P.H.E → P.H.E.);
 * - a code without a glossary entry is never shown raw: "-" and a Sentry error;
 * - text written by a person is kept as is.
 * @param {string|null|undefined} value - attendance record observations
 * @returns {string|null} null when there is no observation
 */
export const formatObservation = value => {
  const observation = typeof value === 'string' ? value.trim() : '';
  if (!observation) return null;

  const glossaryItem = GLOSSARY_ITEMS.find(item => item.code === observation);
  if (glossaryItem) return glossaryItem.short;

  const spelling =
    GLOSSARY_SPELLING_BY_LABEL.get(labelKey(observation)) ??
    GLOSSARY_SPELLING.get(spellingKey(observation));
  if (spelling) return spelling;

  if (INTERNAL_CODE.test(observation)) {
    reportMissingAbbreviation(observation);
    return MISSING_OBSERVATION;
  }

  return observation;
};
